import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { UiPanelComponent } from '@template-ui/components';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import {
  WorkflowDefinitionDocument,
  WorkflowNavigationItem,
} from './platform-workflow-definition-lifecycle.models';
import {
  WorkflowMenuRow,
  applyWorkflowNavigation,
  workflowMenuFingerprint,
  workflowMenuIcons,
  workflowMenuRows,
  workflowNavigationError,
  workflowNavigationShapeError,
} from './workflow-navigation-authoring';
import type { WorkflowEditorPendingScope, WorkflowEditorBufferState } from './workflow-editor-pending-state';
import { WorkflowOwnerViewState } from './workflow-owner-view-state';
import { WorkflowMenuIconPickerComponent } from './workflow-menu-icon-picker.component';

export interface WorkflowNavigationDraftSnapshot {
  iconKey: string; rows: readonly WorkflowMenuRow[]; defaults: boolean;
  replacingUnsupportedMenu: boolean; unresolved: readonly WorkflowNavigationItem[]; baseline: string; context: string;
}
export interface WorkflowNavigationOwnedDocumentChange {
  document: WorkflowDefinitionDocument; owner: string; source: string; view: string | null; draft: WorkflowNavigationDraftSnapshot;
}
interface NavigationOrigin { generation: number; owner: string; source: string; view: string | null; }
interface NavigationRowOrigin { row: WorkflowMenuRow; origin: NavigationOrigin; }

@Component({
  selector: 'phms-workflow-navigation-settings',
  imports: [
    CommonModule,
    FormsModule,
    UiPanelComponent,
    UiButtonDirective,
    UiControlDirective,
    WorkflowMenuIconPickerComponent,
  ],
  templateUrl: './workflow-navigation-settings.component.html',
  styleUrl: './workflow-navigation-settings.component.scss',
})
export class WorkflowNavigationSettingsComponent implements OnChanges {
  @Input({ required: true }) document!: WorkflowDefinitionDocument;
  @Input() disabled = false;
  @Input() active = true;
  @Input() workspaceMode = false;
  @Input() latestDocument: (() => WorkflowDefinitionDocument | null) | null = null;
  @Input() ownerContext: (() => unknown) | null = null;
  @Input() viewContext: (() => unknown) | null = null;
  @Input() activeCallback: (() => boolean) | null = null;
  @Input() activeView: (() => string | null) | null = null;
  @Input() activeViewState: string | null | undefined = undefined;
  @Input() processName: string | undefined;
  @Input() processNameEditable = true;
  @Input() processNameError = '';
  @Output() processNameChange = new EventEmitter<string>();
  @Output() openStepSetup = new EventEmitter<void>();
  @Output() documentChange = new EventEmitter<WorkflowDefinitionDocument>();
  @Output() ownedDocumentChange = new EventEmitter<WorkflowNavigationOwnedDocumentChange>();
  @Output() pendingChangesChange = new EventEmitter<boolean>();
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true }) ?? new WorkflowOwnerViewState();
  readonly icons = workflowMenuIcons;
  iconKey = 'pi pi-sitemap';
  rows: WorkflowMenuRow[] = [];
  error = '';
  private pending = false;
  private original = '';
  private originalContext = '';
  private defaults = false;
  private replacingUnsupportedMenu = false;
  private unresolved: WorkflowNavigationItem[] = [];
  private focusRequest = 0;
  private formGeneration = 0;
  private renderedView: string | null = null;
  private ownerSnapshot = '';
  private namespaceSnapshot = '';
  private emitting: WorkflowNavigationOwnedDocumentChange | null = null;
  private restoring = false;
  private projectionError = '';

  ngOnChanges(changes: SimpleChanges = {}): void {
    const view = this.activeView?.() ?? null;
    if ((this.activeViewState === undefined || this.activeViewState === view) && this.renderedView !== view) {
      this.renderedView = view; this.formGeneration++; this.focusRequest++;
    }
    if (this.workspaceMode && this.pending && changes['document'] && this.namespaceSnapshot === this.namespace(this.document)
      && this.documentContext(this.document) === this.originalContext && this.fingerprint(this.document) === this.original) this.ownerSnapshot = this.ownerKey();
    if (!this.pending) this.reload();
  }
  private currentDocument(): WorkflowDefinitionDocument | null { return this.workspaceMode && this.latestDocument ? this.latestDocument() : this.document; }
  private ownerKey(): string { return JSON.stringify(this.ownerContext?.() ?? this.documentContext(this.currentDocument())); }
  private namespace(document = this.currentDocument()): string { return JSON.stringify([this.documentContext(document), this.viewContext?.() ?? this.ownerKey()]); }
  private fingerprint(document: WorkflowDefinitionDocument | null): string { try { return document ? workflowMenuFingerprint(document) : ''; } catch { return ''; } }
  private ownerCurrent(document = this.currentDocument()): boolean {
    return !this.workspaceMode || !!document && this.ownerSnapshot === this.ownerKey() && this.namespaceSnapshot === this.namespace(document)
      && this.originalContext === this.documentContext(document) && this.original === this.fingerprint(document);
  }
  get viewActive(): boolean { return this.active && (this.activeCallback?.() ?? true); }
  get renderState(): { origin: NavigationOrigin } {
    return { origin: { generation: this.formGeneration, owner: this.ownerKey(), source: JSON.stringify(this.currentDocument()), view: this.activeView?.() ?? null } };
  }
  rowRenderState(row: WorkflowMenuRow, origin: NavigationOrigin): NavigationRowOrigin { return { row, origin }; }
  private nativeCurrent(origin?: NavigationOrigin): boolean {
    if (this.restoring) return false;
    if (!this.workspaceMode) return true;
    const current = origin ?? this.renderState.origin, view = this.activeView?.() ?? null;
    return this.ownerCurrent() && this.viewActive && (!this.activeView || view !== null && view === this.renderedView)
      && (this.activeViewState === undefined || this.activeViewState === view)
      && !this.element.nativeElement.closest('[hidden],[inert],[aria-hidden="true"]') && !this.ownerViews.focusSuspended()
      && current.generation === this.formGeneration && current.owner === this.ownerKey()
      && current.source === JSON.stringify(this.currentDocument()) && current.view === view;
  }
  private restore(model: NgModel | undefined, value: unknown): void {
    if (!model || this.restoring) return;
    this.restoring = true; try { model.control.setValue(value, { emitEvent: false }); } finally { this.restoring = false; }
  }
  get draftSnapshot(): WorkflowNavigationDraftSnapshot {
    return structuredClone({ iconKey: this.iconKey, rows: this.rows, defaults: this.defaults,
      replacingUnsupportedMenu: this.replacingUnsupportedMenu, unresolved: this.unresolved, baseline: this.original, context: this.originalContext });
  }
  private reload(): void {
    if (!this.document) return;
    this.projectionError = '';
    try {
    const unsupported = workflowNavigationShapeError(this.document.navigation);
    this.iconKey = typeof this.document.navigation?.iconKey === 'string' ? this.document.navigation.iconKey : 'pi pi-sitemap';
    this.rows = workflowMenuRows(unsupported ? { ...this.document, navigation: null } : this.document);
    const seen = new Set<string>();
    this.unresolved = (unsupported ? [] : this.document.navigation?.items ?? []).filter((item) => {
      const key = this.key(item);
      const duplicate = seen.has(key);
      seen.add(key);
      return (
        duplicate ||
        this.rows.filter((row) => this.key(row) === key).length !== 1
      );
    });
    this.original = this.fingerprint(this.document);
    this.originalContext = this.documentContext(this.document);
    this.defaults = false;
    this.replacingUnsupportedMenu = false;
    this.error =
      workflowNavigationError(
        this.document,
        this.document.navigation ?? null,
      ) ?? '';
    } catch {
      this.rows = []; this.unresolved = []; this.original = ''; this.originalContext = this.documentContext(this.document);
      this.projectionError = 'Saved dashboard or menu settings are unavailable. Their original values are retained.';
      this.error = this.projectionError;
    }
    this.ownerSnapshot = this.ownerKey(); this.namespaceSnapshot = this.namespace(this.document); this.formGeneration++;
  }
  private changed(): void {
    this.pending = true;
    this.defaults = false;
    this.error = '';
    this.pendingChangesChange.emit(true);
  }
  setIcon(value: string, origin?: NavigationOrigin): void {
    if (!this.nativeCurrent(origin) || this.menuDisabled || !this.isKnownIcon(value) || value === this.iconKey) return;
    this.iconKey = value;
    this.changed();
  }
  updateRow(
    index: number,
    patch: Partial<Pick<WorkflowMenuRow, 'label' | 'iconKey' | 'visible'>>,
  ): void {
    if (!this.nativeCurrent() || this.menuDisabled || !this.rows[index]) return;
    if (this.workspaceMode) { Object.assign(this.rows[index], patch); this.changed(); return; }
    this.rows = this.rows.map((row, i) =>
      i === index ? { ...row, ...patch } : row,
    );
    this.changed();
  }
  updateRenderedRow(rendered: NavigationRowOrigin, patch: Partial<Pick<WorkflowMenuRow, 'label' | 'iconKey' | 'visible'>>, model?: NgModel): void {
    const row = rendered.row, key = Object.keys(patch)[0] as 'label' | 'iconKey' | 'visible';
    const matches = this.rows.filter(item => this.key(item) === this.key(row));
    if (!this.nativeCurrent(rendered.origin) || !this.rows.includes(row) || matches.length !== 1 || this.menuDisabled) {
      this.restore(model, (matches.length === 1 ? matches[0] : row)[key] ?? (key === 'label' ? '' : null)); return;
    }
    this.updateRow(this.rows.indexOf(row), patch);
  }
  move(index: number, direction: -1 | 1): void {
    if (!this.nativeCurrent() || this.menuDisabled || !this.rows[index] || !this.rows[index + direction])
      return;
    const rows = [...this.rows];
    [rows[index], rows[index + direction]] = [
      rows[index + direction],
      rows[index],
    ];
    this.rows = rows.map((row, i) => ({ ...row, sortOrder: i * 10 }));
    this.changed();
  }
  onReorder(index: number, direction: -1 | 1, rendered?: NavigationRowOrigin): void {
    if (rendered && (!this.nativeCurrent(rendered.origin) || this.rows[index] !== rendered.row)) return;
    const row = this.rows[index];
    if (!row || this.menuDisabled || !this.rows[index + direction]) return;
    const key = this.key(row);
    this.move(index, direction);
    const request = ++this.focusRequest, context = this.documentContext(this.document), owner = this.ownerKey(), source = JSON.stringify(this.currentDocument()), view = this.activeView?.() ?? null,
      before = this.element.nativeElement.ownerDocument.activeElement;
    setTimeout(() => {
      if (this.destroyRef.destroyed || request !== this.focusRequest || context !== this.documentContext(this.document) ||
        this.ownerViews.focusSuspended() || this.element.nativeElement.closest('[hidden],[inert]') || this.workspaceMode && (!this.nativeCurrent() || owner !== this.ownerKey() || source !== JSON.stringify(this.currentDocument()) || view !== (this.activeView?.() ?? null))) return;
      const active = this.element.nativeElement.ownerDocument.activeElement;
      if (this.workspaceMode && active !== before && active !== this.element.nativeElement.ownerDocument.body && active?.isConnected) return;
      const moved = Array.from(this.element.nativeElement.querySelectorAll<HTMLElement>('[data-menu-row]'))
        .find(element => element.dataset['menuRow'] === key);
      const preferred = moved?.querySelector<HTMLButtonElement>(`[data-move="${direction}"]:not(:disabled)`);
      (preferred ?? moved?.querySelector<HTMLButtonElement>('[data-move]:not(:disabled)'))?.focus();
    });
  }
  restoreDefaults(origin?: NavigationOrigin): void {
    if (!this.nativeCurrent(origin) || this.disabled || !this.menuFormatSupported || this.ambiguousDashboardLinks || this.projectionError) return;
    this.iconKey = 'pi pi-sitemap';
    this.rows = workflowMenuRows({ ...this.document, navigation: null });
    this.unresolved = [];
    this.replacingUnsupportedMenu = !!workflowNavigationShapeError(this.document.navigation);
    this.pending = true;
    this.defaults = true;
    this.error = '';
    this.pendingChangesChange.emit(true);
  }
  get sharedProcessName(): string { return this.processName ?? this.document?.displayName ?? ''; }
  updateProcessName(value: string, origin?: NavigationOrigin, model?: NgModel): void {
    if (!this.nativeCurrent(origin) || this.disabled || !this.processNameEditable) { this.restore(model, this.sharedProcessName); return; }
    this.processNameChange.emit(value);
  }
  requestStepSetup(origin?: NavigationOrigin): void { if (this.nativeCurrent(origin) && !this.disabled) this.openStepSetup.emit(); }
  get menuFormatSupported(): boolean { return /^1\.(?:[0-9]|1[0-9]|2[0-2])$/.test(this.document?.payloadSchemaVersion ?? ''); }
  get ambiguousDashboardLinks(): boolean { return new Set(this.rows.map(row => this.key(row))).size !== this.rows.length; }
  get menuReadOnlyReason(): string {
    if (this.projectionError) return this.projectionError;
    if (!this.menuFormatSupported) return 'Menu editing is unavailable for this saved workflow format. Its original settings are retained.';
    if (this.ambiguousDashboardLinks) return 'Some dashboard links have duplicate owner identities. Review those dashboards in their owning steps before changing this menu.';
    return this.replacingUnsupportedMenu ? '' : workflowNavigationShapeError(this.document.navigation) ?? '';
  }
  get menuDisabled(): boolean { return this.disabled || !!this.menuReadOnlyReason || this.workspaceMode && !this.ownerCurrent(); }
  get retainedMenuJson(): string { return JSON.stringify(this.document?.navigation ?? null, null, 2); }
  get unsupportedMenu(): boolean { return !!workflowNavigationShapeError(this.document?.navigation); }
  get showingRetainedMenu(): boolean { return this.unsupportedMenu && !this.replacingUnsupportedMenu; }
  get menuDefaultsStaged(): boolean { return this.pending && this.defaults; }
  get unresolvedCount(): number { return this.unresolved.length; }
  get pickerContext(): string { return this.document ? JSON.stringify([this.documentContext(this.document), this.original,
    this.workspaceMode ? [this.ownerKey(), this.renderedView, this.formGeneration] : null]) : ''; }
  rowLabel(row: WorkflowMenuRow): string { return `${row.dashboardLabel} — ${row.stepLabel}`; }
  hasPendingChanges(): boolean {
    return this.pending;
  }
  private documentContext(document: WorkflowDefinitionDocument | null): string {
    return JSON.stringify([document?.workflowCode, document?.definitionVersionCode, document?.packReference]);
  }
  /** Navigation is one ordered menu buffer, separate from its dashboard sources. */
  pendingEditorScopes(latestDocument: () => WorkflowDefinitionDocument | null = () => this.currentDocument()): readonly WorkflowEditorPendingScope[] {
    return [{
      id: 'workflow-navigation', label: 'workflow menu input',
      state: (): WorkflowEditorBufferState => {
        if (!this.pending) return 'clean';
        const document = latestDocument();
        if (!document || !this.ownerCurrent(document) || this.documentContext(document) !== this.originalContext || this.fingerprint(document) !== this.original) return 'stale';
        if (this.menuDisabled) return 'invalid';
        try { applyWorkflowNavigation(document, this.navigationCandidate(document)); return 'valid'; } catch { return 'invalid'; }
      },
      fingerprint: () => {
        const document = latestDocument();
        return JSON.stringify([this.pending, this.original, this.originalContext, this.iconKey, this.rows, this.defaults, this.replacingUnsupportedMenu, this.unresolved,
          this.disabled, this.menuReadOnlyReason, this.ownerSnapshot, this.ownerKey(), this.namespaceSnapshot, document && [this.documentContext(document), this.fingerprint(document), document.payloadSchemaVersion]]);
      },
      apply: () => { const document = latestDocument(); return !!document && this.applyPendingChanges(document); },
      discard: () => { const document = latestDocument(); if (document) this.document = document; this.discardPendingChanges(); },
      focus: () => this.focusPendingChanges(),
    }];
  }
  private navigationCandidate(document: WorkflowDefinitionDocument) {
    const defaults = workflowMenuRows({ ...document, navigation: null });
    const retained = new Set((this.replacingUnsupportedMenu || !Array.isArray(document.navigation?.items) ? [] : document.navigation.items).map(item => this.key(item)));
    return this.defaults ? null : {
      iconKey: this.iconKey,
      items: [
        ...this.rows.filter(row => retained.has(this.key(row)) || row.label?.trim() || row.iconKey || !row.visible ||
          row.sortOrder !== defaults.find(item => this.key(item) === this.key(row))?.sortOrder)
          .map(row => ({ nodeId: row.nodeId, dashboardId: row.dashboardId, label: row.label, iconKey: row.iconKey, sortOrder: row.sortOrder, visible: row.visible })),
        ...this.unresolved,
      ],
    };
  }
  isCurrentOwnedDocumentChange(event: WorkflowNavigationOwnedDocumentChange, current: WorkflowDefinitionDocument): boolean {
    if (!this.workspaceMode || this.emitting !== event || !this.pending || this.disabled || !this.ownerCurrent(current)
      || event.owner !== this.ownerKey() || event.source !== JSON.stringify(current) || JSON.stringify(event.draft) !== JSON.stringify(this.draftSnapshot)) return false;
    try { return JSON.stringify(applyWorkflowNavigation(current, this.navigationCandidate(current))) === JSON.stringify(event.document); } catch { return false; }
  }
  applyPendingChanges(document = this.currentDocument()): boolean {
    if (this.emitting) return false;
    if (!this.pending) return true;
    if (!document) return false;
    if (!this.ownerCurrent(document) || this.workspaceMode && JSON.stringify(document) !== JSON.stringify(this.currentDocument()) || this.documentContext(document) !== this.originalContext || this.fingerprint(document) !== this.original) {
      this.error =
        'The workflow menu or its dashboards changed while you were editing. Discard these edits and review the current menu.';
      this.focusPendingChanges();
      return false;
    }
    if (this.menuDisabled) return false;
    try {
      const next = applyWorkflowNavigation(document, this.navigationCandidate(document));
      if (this.workspaceMode) {
        const owner = this.ownerKey(), namespace = this.namespace(), draft = JSON.stringify(this.draftSnapshot), expected = JSON.stringify(next);
        const event: WorkflowNavigationOwnedDocumentChange = { document: next, owner, source: JSON.stringify(document), view: this.activeView?.() ?? null, draft: this.draftSnapshot };
        this.emitting = event; try { this.ownedDocumentChange.emit(event); } finally { this.emitting = null; }
        const receipt = this.currentDocument();
        if (!receipt || receipt === document || owner !== this.ownerKey() || namespace !== this.namespace() || draft !== JSON.stringify(this.draftSnapshot) || expected !== JSON.stringify(receipt)) {
          this.error = 'Workflow menu input was not applied to the current draft. Your input is retained.'; return false;
        }
        this.document = receipt; this.pending = false; this.reload(); this.pendingChangesChange.emit(false); return true;
      }
      this.document = next;
      this.pending = false;
      this.error = '';
      this.pendingChangesChange.emit(false);
      this.documentChange.emit(next);
      this.original = workflowMenuFingerprint(next);
      this.replacingUnsupportedMenu = false;
      return true;
    } catch (error) {
      this.error =
        error instanceof Error
          ? error.message
          : 'Review the workflow menu settings.';
      this.focusPendingChanges();
      return false;
    }
  }
  discardPendingChanges(): void {
    this.focusRequest++;
    if (this.workspaceMode) { const document = this.currentDocument(); if (document) this.document = document; }
    this.pending = false;
    this.reload();
    this.pendingChangesChange.emit(false);
  }
  resetPendingChanges(): void {
    this.discardPendingChanges();
  }
  focusPendingChanges(): void {
    const request = ++this.focusRequest, owner = this.ownerKey(), source = JSON.stringify(this.currentDocument()), view = this.activeView?.() ?? null,
      before = this.element.nativeElement.ownerDocument.activeElement;
    queueMicrotask(() => {
      if (this.destroyRef.destroyed || request !== this.focusRequest || this.ownerViews.focusSuspended() || this.element.nativeElement.closest('[hidden],[inert]')
        || this.workspaceMode && (!this.nativeCurrent() || owner !== this.ownerKey() || source !== JSON.stringify(this.currentDocument()) || view !== (this.activeView?.() ?? null))) return;
      const active = this.element.nativeElement.ownerDocument.activeElement;
      if (this.workspaceMode && active !== before && active !== this.element.nativeElement.ownerDocument.body && active?.isConnected) return;
      this.element.nativeElement
        .querySelector<HTMLElement>('[role=alert], phms-workflow-menu-icon-picker button, input')
        ?.focus();
    });
  }
  capturePendingNavigationInputs(): readonly { name: string; label: string; value: string }[] {
    return this.pending ? [{ name: 'workflow-navigation-input', label: 'Unapplied Staff navigation settings',
      value: JSON.stringify({ format: 'phms.workflow-navigation-input', version: 1, draft: this.draftSnapshot,
        owner: this.ownerSnapshot, namespace: this.namespaceSnapshot }) }] : [];
  }
  safeIcon(value: string | null | undefined, fallback = 'pi pi-list'): string {
    return this.icons.some((icon) => icon.code === value) ? value! : fallback;
  }
  isKnownIcon(value: string): boolean {
    return this.icons.some((icon) => icon.code === value);
  }
  key(item: { nodeId: string; dashboardId: string }): string {
    return JSON.stringify([item.nodeId, item.dashboardId]);
  }
  trackRow = (_: number, row: WorkflowMenuRow) => this.key(row);
  get visibleRows(): WorkflowMenuRow[] {
    return this.rows.filter((row) => row.visible);
  }
}
